import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldAlert, AlertTriangle, Lock, Bug, Activity, Users } from "lucide-react";
import { KPI, SeverityBadge } from "@/components/Primitives";
import DrillPanel from "@/components/DrillPanel";

// CTO view: endpoint security posture across the managed estate. Everything here
// is computed live from agent telemetry (protection state, patch level,
// encryption, auth-failure/FIM events) — no static figures. Every tile drills
// into the exact devices/records behind its number.

function Bar({ label, value, warnBelow = 90, onClick }) {
  const tone = value >= warnBelow ? "bg-emerald-500" : value >= 50 ? "bg-amber-500" : "bg-red-500";
  return (
    <div onClick={onClick} className={onClick ? "cursor-pointer group" : ""}>
      <div className="flex justify-between text-xs mb-1">
        <span className={"text-zinc-400 " + (onClick ? "group-hover:text-zinc-200" : "")}>{label}</span>
        <span className="font-mono text-zinc-200">{value}%</span>
      </div>
      <div className="h-2 rounded-full bg-zinc-900 overflow-hidden">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function scoreTone(s) {
  if (s >= 85) return "good";
  if (s >= 60) return "warn";
  return "bad";
}

export default function ExecSecurity() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [drill, setDrill] = useState(null);

  const load = () => api.get("/exec/security-summary")
    .then(r => { setD(r.data); setErr(false); })
    .catch(() => setErr(true));
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  if (err) return <div className="p-8 text-zinc-500 text-sm">Could not load the security summary.</div>;
  if (!d) return <div className="p-8 text-zinc-500 text-sm">Loading security posture…</div>;

  const p = d.protection || {};
  const v = d.vulnerabilities || {};
  const x = d.exposure || {};

  return (
    <div className="p-6 md:p-8 max-w-[1200px] mx-auto">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center">
          <ShieldAlert className="text-red-400" size={18} />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-zinc-50">Security Command</h1>
          <div className="text-xs text-zinc-500 uppercase tracking-widest font-mono">CTO view · endpoint security posture</div>
        </div>
      </div>

      {/* headline */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-6">
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5 md:col-span-1 flex flex-col justify-center items-center">
          <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-mono">Posture score</div>
          <div className={`mt-1 font-mono font-semibold text-5xl ${{ good: "text-emerald-400", warn: "text-amber-400", bad: "text-red-400" }[scoreTone(d.posture_score)]}`}>
            {d.posture_score}
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">of 100 · weighted risk</div>
        </div>
        <div onClick={() => setDrill("devices")} className="cursor-pointer"><KPI testid="sec-devices" label="Managed devices" value={d.estate.devices} sub={`${d.estate.online} online · ${d.estate.offline} offline`} /></div>
        <div onClick={() => setDrill("incidents")} className="cursor-pointer"><KPI testid="sec-incidents" label="Open incidents" value={x.open_incidents ?? 0} tone={x.open_incidents ? "bad" : "good"} sub={`${x.security_findings ?? 0} findings`} /></div>
        <div onClick={() => setDrill("admin_sprawl")} className="cursor-pointer"><KPI testid="sec-admin" label="Local-admin sprawl" value={d.admin_sprawl_devices ?? 0} unit="dev" tone={d.admin_sprawl_devices ? "warn" : "good"} sub="> 2 local admins" /></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {/* protection coverage */}
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
          <div className="flex items-center gap-2 mb-4"><Lock size={15} className="text-zinc-400" /><h2 className="text-sm font-medium text-zinc-200">Protection coverage</h2></div>
          <div className="space-y-3">
            <Bar label="Patched (no missing updates)" value={p.patched ?? 0} onClick={() => setDrill("patch_gap")} />
            <Bar label="Disk encryption on" value={p.encryption ?? 0} warnBelow={100} onClick={() => setDrill("encryption_gap")} />
            <Bar label="Anti-malware active" value={p.antimalware ?? 0} warnBelow={100} onClick={() => setDrill("antimalware_gap")} />
            <Bar label="Hardening benchmark" value={p.hardening ?? 0} warnBelow={80} onClick={() => setDrill("hardening_gap")} />
          </div>
        </div>

        {/* vulnerabilities + exposure */}
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
          <div className="flex items-center gap-2 mb-4"><Bug size={15} className="text-zinc-400" /><h2 className="text-sm font-medium text-zinc-200">Vulnerabilities &amp; exposure</h2></div>
          <div className="grid grid-cols-4 gap-2 mb-4">
            {["critical", "high", "medium", "low"].map(s => (
              <div key={s} onClick={() => setDrill(`vuln_${s}`)} className="text-center bg-zinc-900/40 border border-zinc-900 rounded p-2 cursor-pointer hover:border-zinc-700">
                <div className={`font-mono text-2xl ${{ critical: "text-red-400", high: "text-orange-400", medium: "text-amber-400", low: "text-emerald-400" }[s]}`}>{v[s] ?? 0}</div>
                <div className="text-[9px] uppercase tracking-wider text-zinc-500 mt-1">{s}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-zinc-900/40 border border-zinc-900 rounded p-2">
              <div className="font-mono text-xl text-zinc-100">{x.auth_failures_7d ?? 0}</div>
              <div className="text-[9px] uppercase tracking-wider text-zinc-500 mt-1">Auth attacks 7d</div>
            </div>
            <div className="bg-zinc-900/40 border border-zinc-900 rounded p-2">
              <div className="font-mono text-xl text-zinc-100">{x.file_integrity_alerts_7d ?? 0}</div>
              <div className="text-[9px] uppercase tracking-wider text-zinc-500 mt-1">FIM alerts 7d</div>
            </div>
            <div className="bg-zinc-900/40 border border-zinc-900 rounded p-2">
              <div className="font-mono text-xl text-zinc-100">{x.exposed_listeners_7d ?? 0}</div>
              <div className="text-[9px] uppercase tracking-wider text-zinc-500 mt-1">Exposed svc 7d</div>
            </div>
          </div>
        </div>
      </div>

      {/* top risks */}
      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5 mt-4">
        <div className="flex items-center gap-2 mb-4"><AlertTriangle size={15} className="text-zinc-400" /><h2 className="text-sm font-medium text-zinc-200">Top risks to act on</h2></div>
        {(!d.top_risks || d.top_risks.length === 0) ? (
          <div className="flex items-center gap-2 text-emerald-400 text-sm"><Activity size={15} /> No material risks detected across the estate.</div>
        ) : (
          <div className="space-y-2">
            {d.top_risks.map((r, i) => (
              <div key={i} className="flex items-center justify-between gap-3 bg-zinc-900/30 border border-zinc-900 rounded px-3 py-2.5">
                <div className="flex items-center gap-3 min-w-0">
                  <SeverityBadge level={r.severity} />
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-100 truncate">{r.title}</div>
                    <div className="text-xs text-zinc-500 truncate">{r.detail}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="text-[11px] text-zinc-600 mt-4 font-mono">
        Live from agent telemetry · refreshed every 15s · click any tile to drill into the devices behind it
      </div>

      <DrillPanel metric={drill} onClose={() => setDrill(null)} />
    </div>
  );
}
